import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { addStuff, updateUser } from '../../../redux/userRelated/userHandle';
import { underControl } from '../../../redux/userRelated/userSlice';
import styled, { keyframes } from 'styled-components';
import {
  Box, Typography, Paper, Container, Button,
  TextField, CircularProgress, Fade, Grid, IconButton
} from '@mui/material';
import {
  Campaign as CampaignIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Title as TitleIcon,
  Description as DescriptionIcon,
  Event as EventIcon
} from '@mui/icons-material';
import { useToast } from '../../../context/ToastContext';
import { CustomNepaliDatePicker, getCurrentBSDate, formatDatePart } from '../../../utils/customDate';

// Animations
const fadeIn = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;

// Styled Components
const PageWrapper = styled(Box)`
  background-color: #f8fafc;
  min-height: 100vh;
  padding-bottom: 80px;
  animation: ${fadeIn} 0.5s ease-out;
`;

const PremiumHeader = styled(Box)`
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  padding: 80px 0 120px;
  color: white;
  position: relative;
  overflow: hidden;
  
  &::after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      width: 400px;
      height: 400px;
      background: radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%);
      border-radius: 50%;
      transform: translate(30%, -30%);
  }
`;

const ContentContainer = styled(Container)`
  margin-top: -80px;
  position: relative;
  z-index: 2;
`;

const PremiumCard = styled(Paper)`
  border-radius: 24px !important;
  border: 1px solid rgba(255, 255, 255, 0.8) !important;
  background: rgba(255, 255, 255, 0.9) !important;
  backdrop-filter: blur(20px) !important;
  padding: 40px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.05) !important;
`;

const StyledTextField = styled(TextField)`
  & .MuiOutlinedInput-root {
      border-radius: 16px;
      background: #f8fafc;
      transition: all 0.3s ease;
      
      &:hover {
          background: #f1f5f9;
      }
      
      &.Mui-focused {
          background: white;
          box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1);
      }
  }
  
  & .MuiOutlinedInput-notchedOutline {
      border-color: #e2e8f0;
  }
`;

const ActionBtn = styled(Button)`
  border-radius: 16px !important;
  padding: 16px 32px !important;
  font-weight: 800 !important;
  font-size: 1.1rem !important;
  text-transform: none !important;
  box-shadow: 0 10px 20px -10px rgba(99, 102, 241, 0.5) !important;
  transition: all 0.3s ease !important;

  &:hover {
      transform: translateY(-2px);
      box-shadow: 0 15px 25px -10px rgba(99, 102, 241, 0.6) !important;
  }
`;

const AddNotice = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { status, response, error } = useSelector(state => state.user);
  const { currentUser } = useSelector(state => state.user);
  const { showToast } = useToast();

  const noticeToEdit = location.state?.notice;
  const isEditing = Boolean(noticeToEdit);

  const [title, setTitle] = useState(noticeToEdit ? noticeToEdit.title : '');
  const [details, setDetails] = useState(noticeToEdit ? noticeToEdit.details : '');
  const [date, setDate] = useState(noticeToEdit ? formatDatePart(noticeToEdit.date) : getCurrentBSDate());
  
  const adminID = currentUser._id;
  const [loader, setLoader] = useState(false);

  const fields = { title, details, date, adminID };
  const address = "Notice";

  const submitHandler = (event) => {
    event.preventDefault();
    setLoader(true);
    if (isEditing) {
        dispatch(updateUser(fields, noticeToEdit._id, address));
    } else {
        dispatch(addStuff(fields, address));
    }
  };

  useEffect(() => {
    if (status === 'added' || status === 'updated') {
      showToast(`Notice ${isEditing ? 'updated' : 'added'} successfully!`, "success");
      dispatch(underControl());
      navigate(-1);
    } else if (status === 'error') {
      showToast("Network Error or Failed Request", "error");
      setLoader(false);
      dispatch(underControl());
    }
  }, [status, navigate, error, response, dispatch, isEditing, showToast]);

  return (
    <PageWrapper>
      <PremiumHeader>
          <Container maxWidth="md" >
              <Fade in timeout={800}>
                  <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                          <IconButton onClick={() => navigate(-1)} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)' }}>
                              <ArrowBackIcon />
                          </IconButton>
                          <Typography variant="h2" sx={{ fontWeight: 900, letterSpacing: '-0.03em', display: 'flex', alignItems: 'center', gap: 2 }}>
                              <CampaignIcon sx={{ fontSize: 48, color: '#6366f1' }} />
                              {isEditing ? 'Edit Notice' : 'Publish Notice'}
                          </Typography>
                      </Box>
                      <Typography variant="h6" sx={{ opacity: 0.7, fontWeight: 400, ml: 8 }}>
                          {isEditing ? 'Update the details of your existing announcement.' : 'Broadcast a new announcement to your school.'}
                      </Typography>
                  </Box>
              </Fade>
          </Container>
      </PremiumHeader>

      <ContentContainer maxWidth="md" sx={{ mt: 5 }} >
          <PremiumCard>
              <form onSubmit={submitHandler}>
                  <Grid container spacing={4}>
                      <Grid item xs={12}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                              <TitleIcon sx={{ color: '#6366f1' }} />
                              <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>
                                  Notice Title
                              </Typography>
                          </Box>
                          <StyledTextField
                              fullWidth
                              placeholder="Enter a descriptive title..."
                              value={title}
                              onChange={(event) => setTitle(event.target.value)}
                              required
                              variant="outlined"
                          />
                      </Grid>

                      <Grid item xs={12}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                              <DescriptionIcon sx={{ color: '#6366f1' }} />
                              <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>
                                  Notice Details
                              </Typography>
                          </Box>
                          <StyledTextField
                              fullWidth
                              placeholder="Type the full announcement details here..."
                              value={details}
                              onChange={(event) => setDetails(event.target.value)}
                              required
                              variant="outlined"
                              multiline
                              rows={6}
                          />
                      </Grid>

                      <Grid item xs={12}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                              <EventIcon sx={{ color: '#6366f1' }} />
                              <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>
                                  Date
                              </Typography>
                          </Box>
                          <CustomNepaliDatePicker
                              fullWidth
                              value={date}
                              onChange={(val) => setDate(val)}
                              required
                          />
                      </Grid>

                      <Grid item xs={12} sx={{ mt: 2 }}>
                          <ActionBtn
                              type="submit"
                              fullWidth
                              variant="contained"
                              color="primary"
                              disabled={loader}
                              startIcon={!loader && <SaveIcon />}
                              sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}
                          >
                              {loader ? (
                                  <CircularProgress size={28} color="inherit" />
                              ) : (
                                  isEditing ? 'Save Changes' : 'Publish Announcement'
                              )}
                          </ActionBtn>
                      </Grid>
                  </Grid>
              </form>
          </PremiumCard>
      </ContentContainer>
    </PageWrapper>
  );
};

export default AddNotice;